import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Button from "../../../component-lib/Molecules/button/Button";
import Input from "../../../component-lib/Atoms/Input/Input";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import FacilitySelect from "../../../component-lib/Organisms/create-visit-dialog/FacilitySelect";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "0px 24px 16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    width: "820px",
    maxWidth: "none",
  },
}));

export const SEND_BULK_TARGET = {
  PHARMACY: "Pharmacy",
  FACILITY: "Facility",
};

interface FacilityOption {
  label: string;
  value: string;
  department: any[];
}

interface SendBulkDialogProps {
  isOpen: boolean;
  dialogHeading?: string;
  selectedCount?: number;
  selectedItems?: any[];
  hideTable?: boolean;
  handleClose: () => void;
  handleSend: (data: {
    target: string;
    facility: FacilityOption | null;
    items: { id: string; quantity: number }[];
  }) => void;
}

export default function SendBulkDialog({
  isOpen = false,
  dialogHeading = "Send Bulk",
  selectedItems = [],
  hideTable = false,
  handleClose,
  handleSend,
}: SendBulkDialogProps) {
  const [target, setTarget] = useState<string>(SEND_BULK_TARGET.PHARMACY);
  const [selectedFacility, setSelectedFacility] =
    useState<FacilityOption | null>(null);
  // Quantity-to-send per item id. Empty initially; blank means 0 on bulk send.
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  // Editable working copy of the items (lets the user delete rows before sending).
  const [items, setItems] = useState<any[]>([]);
  // Client-side pagination for the items table.
  const [tablePage, setTablePage] = useState(1);
  const [tableLimit, setTableLimit] = useState(50);

  useEffect(() => {
    if (isOpen) {
      // Only sendable items: drop anything with 0 current stock.
      setItems(
        (selectedItems || []).filter((it) => Number(it?.stockLevel) > 0)
      );
      setQuantities({});
      setTarget(SEND_BULK_TARGET.PHARMACY);
      setSelectedFacility(null);
      setTablePage(1);
      setTableLimit(50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const pagedItems = (items || []).slice(
    (tablePage - 1) * tableLimit,
    tablePage * tableLimit
  );

  const isFacility = target === SEND_BULK_TARGET.FACILITY;

  // Bulk: blanks are allowed (sent as 0); only an ENTERED value is invalid when it's
  // non-numeric, negative, or greater than the item's current stock.
  const enteredInvalid = (items || []).some((it: any) => {
    const raw = quantities[it?.id];
    if (raw === undefined || raw === "") return false; // blank → 0, allowed
    const qty = Number(raw);
    return Number.isNaN(qty) || qty < 0 || qty > Number(it?.stockLevel);
  });
  // Single: require a real quantity (1..stock) for the one item.
  const singleInvalid =
    hideTable &&
    (() => {
      const it = items?.[0];
      const raw = quantities[it?.id];
      const qty = Number(raw);
      return (
        raw === undefined ||
        raw === "" ||
        Number.isNaN(qty) ||
        qty < 1 ||
        qty > Number(it?.stockLevel)
      );
    })();
  const disableSend =
    (isFacility && !selectedFacility?.value) || enteredInvalid || singleInvalid;

  // Per-item transfer table: Item Name | Current Quantity | Quantity to Send.
  const transferColumns = [
    {
      field: "itemName",
      label: <div>Item Name</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.itemName}</p>
      ),
    },
    {
      field: "stockLevel",
      label: <div>Current Quantity</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.stockLevel}</p>
      ),
    },
    {
      field: "sendQty",
      label: <div>Quantity to Send</div>,
      renderLogic: (row: any) => (
        <div className="w-[140px]">
          <Input
            type="number"
            size="small"
            placeholder="Quantity"
            disabled={Number(row?.stockLevel) === 0}
            value={quantities[row?.id] ?? ""}
            onChangeCb={(e: any) =>
              setQuantities((prev) => ({
                ...prev,
                [row?.id]: e?.target?.value,
              }))
            }
          />
        </div>
      ),
    },
    {
      field: "remove",
      label: <div></div>,
      renderLogic: (row: any) => (
        <IconButton
          aria-label="remove-item"
          disabled={items.length === 1}
          onClick={() => {
            setItems((prev) => prev.filter((it: any) => it?.id !== row?.id));
            setQuantities((prev) => {
              const next = { ...prev };
              delete next[row?.id];
              return next;
            });
            setTablePage(1);
          }}
          sx={{ color: items.length === 1 ? "#CCCCCC" : "#CE0718" }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="send-bulk-dialog-title"
      open={isOpen}
      PaperProps={{
        sx: {
          width: hideTable ? "480px" : "820px",
          maxWidth: "none",
          height: hideTable ? "auto" : "min(85vh, 620px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          fontWeight: 600,
          fontSize: "18px",
          fontFamily: "inter",
        }}
        id="send-bulk-dialog-title"
      >
        {dialogHeading}
        {!hideTable && items.length ? ` (${items.length})` : ""}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <hr />

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: hideTable ? "auto" : "hidden",
        }}
      >
        <div className="mt-4" style={{ flexShrink: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: "14px",
              color: "#393939",
              marginBottom: "4px",
            }}
          >
            Send To
          </div>
          <FormControl>
            <RadioGroup
              row
              name="send-bulk-target"
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                if (e.target.value === SEND_BULK_TARGET.PHARMACY) {
                  setSelectedFacility(null);
                }
              }}
              sx={{ gap: "24px" }}
            >
              <FormControlLabel
                value={SEND_BULK_TARGET.PHARMACY}
                control={
                  <Radio sx={{ "&.Mui-checked": { color: "#005596" } }} />
                }
                label="Send to Central"
              />
              <FormControlLabel
                value={SEND_BULK_TARGET.FACILITY}
                control={
                  <Radio sx={{ "&.Mui-checked": { color: "#005596" } }} />
                }
                label="Send to Facility"
              />
            </RadioGroup>
          </FormControl>

          {isFacility && (
            <div className="mt-3">
              <FacilitySelect
                selectedValue={selectedFacility}
                onSelect={(facility) => setSelectedFacility(facility)}
              />
            </div>
          )}
        </div>

        {hideTable ? (
          <div className="mt-5">
            <div
              style={{
                border: "1px solid #E8E8E8",
                borderRadius: "8px",
                background: "#F8F9FB",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  style={{
                    color: "#727272",
                    fontSize: "13px",
                    width: "140px",
                    flexShrink: 0,
                  }}
                >
                  Item Name
                </span>
                <span
                  style={{
                    color: "#393939",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {items?.[0]?.itemName || "-"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  style={{
                    color: "#727272",
                    fontSize: "13px",
                    width: "140px",
                    flexShrink: 0,
                  }}
                >
                  Current Quantity
                </span>
                <span
                  style={{
                    color: "#393939",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {items?.[0]?.stockLevel ?? "-"}
                </span>
              </div>
            </div>

            <div className="mt-4 w-[240px]">
              <Input
                type="number"
                size="small"
                textFieldLabel="Quantity to Send"
                disabled={Number(items?.[0]?.stockLevel) === 0}
                value={quantities[items?.[0]?.id] ?? ""}
                onChangeCb={(e: any) =>
                  setQuantities((prev) => ({
                    ...prev,
                    [items?.[0]?.id]: e?.target?.value,
                  }))
                }
              />
              <div
                style={{
                  color: "#979797",
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                Max: {items?.[0]?.stockLevel ?? 0}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mt-5"
            style={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <Table
              columns={transferColumns}
              rows={pagedItems}
              noDataFoundMessage="No items selected."
              paginationProps={{
                isPagination: true,
                page: tablePage,
                totalPages: Math.ceil((items?.length || 0) / tableLimit) || 1,
                totalCount: items?.length || 0,
                limit: tableLimit,
                rowsPerPageOptions: [25, 50, 100],
                onPageChange: (p: any) => setTablePage(p),
                handleChangeRowsPerPage: (n: any) => {
                  setTableLimit(Number(n));
                  setTablePage(1);
                },
              }}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "24px" }}>
        <Button
          label={TEXT_CONSTANTS?.CANCEL}
          variant="outlined"
          onClickCb={() => handleClose()}
          style={{
            width: "120px",
            height: "44px",
            padding: "12px 20px",
            fontSize: "16px",
            backgroundColor: "white",
            border: "none",
            color: "#393939",
          }}
        />
        <Button
          label="Send"
          variant="contained"
          disabled={disableSend}
          onClickCb={() =>
            handleSend({
              target,
              facility: selectedFacility,
              items: (items || []).map((it: any) => ({
                id: it?.id,
                quantity: Number(quantities[it?.id]) || 0,
              })),
            })
          }
          style={{
            padding: "8px 55px",
            fontSize: "16px",
          }}
        />
      </DialogActions>
    </BootstrapDialog>
  );
}
